// /Users/adrian.leineweber/Development/gemini-test/client/src/components/BarcodeScanner.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Quagga from 'quagga';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Typography,
    AppBar, Toolbar, useMediaQuery, Box, Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

const BarcodeScanner = ({ open, onClose, onBarcodeDetected }) => {
  const [barcodeScanError, setBarcodeScanError] = useState(null);
  const quaggaInitialized = useRef(false);
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  // *** 1. Ref erstellen ***
  const interactiveRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const stopCamera = useCallback(() => {
    console.log('BarcodeScanner.js: stopCamera - Start');
    if (quaggaInitialized.current) {
        try {
            // Entferne Listener *bevor* Quagga gestoppt wird
            Quagga.offDetected(handleDetected); // Wichtig: Verwende den *gleichen* Handler wie bei onDetected
            Quagga.offProcessed();
            console.log("BarcodeScanner.js: stopCamera - Quagga listeners removed");

            Quagga.stop();
            quaggaInitialized.current = false;
            console.log('BarcodeScanner.js: stopCamera - Quagga gestoppt');
        } catch (err) {
            console.error('BarcodeScanner.js: Fehler beim Stoppen von Quagga oder Entfernen der Listener:', err);
        }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      console.log('BarcodeScanner.js: stopCamera - Kamera-Tracks gestoppt');
    }
    if (videoRef.current) {
        videoRef.current.srcObject = null;
        // Optional: Viewport verstecken, wenn Ref vorhanden ist
        if (interactiveRef.current) {
            interactiveRef.current.style.display = 'none';
        }
    }
    console.log('BarcodeScanner.js: stopCamera - Ende');
  }, []); // handleDetected aus Abhängigkeiten entfernt, da es jetzt unten definiert wird

  const handleClose = useCallback(() => {
    console.log('BarcodeScanner.js: handleClose - Start');
    stopCamera();
    // Kurze Verzögerung nicht mehr unbedingt nötig, da stopCamera jetzt synchroner sein sollte
    onClose();
    console.log('BarcodeScanner.js: handleClose - Ende');
  }, [onClose, stopCamera]);

  // handleDetected muss hier definiert sein, damit es in stopCamera verfügbar ist
  // Es wird aber immer noch useCallback verwendet, um die Referenzstabilität für onDetected zu gewährleisten
  const handleDetected = useCallback((data) => {
    console.log('BarcodeScanner.js: handleDetected - Barcode erkannt:', data.codeResult.code);
    onBarcodeDetected(data.codeResult.code);
    // Wichtig: handleClose ruft stopCamera auf, was den Listener entfernt.
    // Daher handleClose erst aufrufen, *nachdem* onBarcodeDetected fertig ist.
    handleClose();
  }, [onBarcodeDetected, handleClose]); // Abhängigkeiten korrekt

  const initializeScanner = useCallback(async () => {
    // *** Wird jetzt von onEntered aufgerufen ***
    console.log('BarcodeScanner.js: initializeScanner (onEntered) - Start');
    setBarcodeScanError(null);

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setBarcodeScanError('getUserMedia wird von diesem Browser nicht unterstützt.');
      console.error('BarcodeScanner.js: initializeScanner - getUserMedia wird nicht unterstützt');
      return;
    }

    if (quaggaInitialized.current) {
      console.log('BarcodeScanner.js: initializeScanner - Bereits initialisiert, überspringe.');
      // Optional: Sicherstellen, dass der Stream läuft, falls er gestoppt wurde
      try { Quagga.start(); } catch(e) { console.warn("Quagga.start() fehlgeschlagen, möglicherweise läuft es bereits", e); }
      return;
    }

    // *** 3. `initializeScanner` anpassen: Ref verwenden ***
    const targetElement = interactiveRef.current;
    if (!targetElement) {
        // Dieser Fehler sollte jetzt nicht mehr auftreten, da onEntered wartet.
        console.error("BarcodeScanner.js: initializeScanner - Target element ref ist nicht verfügbar!");
        setBarcodeScanError("Interner Fehler: Scanner-Anzeigebereich konnte nicht initialisiert werden.");
        return;
    }
    targetElement.style.display = 'block'; // Stelle sicher, dass der Viewport sichtbar ist

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      console.log('BarcodeScanner.js: initializeScanner - Kamera-Stream erhalten');

      Quagga.init({
        inputStream: {
          name: "Live",
          type: "LiveStream",
          target: targetElement, // *** Ref's current element verwenden ***
          constraints: {
            width: { min: 640 },
            height: { min: 480 },
            facingMode: "environment",
            aspectRatio: { min: 1, max: 2 }
          },
          area: { top: "10%", right: "10%", left: "10%", bottom: "10%" },
          singleChannel: false
        },
        locator: { patchSize: "medium", halfSample: true },
        numOfWorkers: navigator.hardwareConcurrency || 4,
        decoder: { readers: ["ean_reader"], multiple: false },
        locate: true,
        frequency: 10
      }, (err) => {
        if (err) {
          console.error('Fehler beim Initialisieren von Quagga:', err);
          setBarcodeScanError(`Fehler beim Initialisieren: ${err.message || err}`);
          stopCamera();
          return;
        }
        quaggaInitialized.current = true;
        console.log('BarcodeScanner.js: initializeScanner - Quagga initialisiert');
        Quagga.start();
        console.log('BarcodeScanner.js: initializeScanner - Quagga gestartet');

        // Event Listener hinzufügen
        Quagga.onDetected(handleDetected); // Verwende den useCallback-Handler
        Quagga.onProcessed((result) => {
            const drawingCtx = Quagga.canvas.ctx.overlay;
            const drawingCanvas = Quagga.canvas.dom.overlay;
            if (result && drawingCanvas) { // Prüfe ob drawingCanvas existiert
                drawingCtx.clearRect(0, 0, parseInt(drawingCanvas.getAttribute("width")), parseInt(drawingCanvas.getAttribute("height")));
                if (result.boxes) {
                    result.boxes.filter(box => box !== result.box).forEach(box => {
                        Quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, drawingCtx, { color: "green", lineWidth: 2 });
                    });
                }
                if (result.box) {
                    Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, { color: "#00F", lineWidth: 2 });
                }
            }
        });
      });
    } catch (error) {
      console.error('Fehler beim Zugriff auf die Kamera oder Initialisierung:', error);
      let message = 'Fehler beim Zugriff auf die Kamera.';
      if (error.name === 'NotAllowedError') message = 'Kamerazugriff wurde verweigert. Bitte erlaube den Zugriff in den Browsereinstellungen.';
      else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') message = 'Keine passende Kamera gefunden (Rückkamera bevorzugt).';
      else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') message = 'Kamera wird bereits verwendet oder kann nicht gelesen werden.';
      setBarcodeScanError(message);
      stopCamera();
    }
    console.log('BarcodeScanner.js: initializeScanner (onEntered) - Ende');
  }, [stopCamera, handleDetected]); // Abhängigkeiten aktualisiert

  // *** 4. `useEffect` anpassen: Nur noch für Cleanup ***
  useEffect(() => {
    // Dieser Effekt wird nur für das Aufräumen beim Unmounten oder wenn sich stopCamera/handleDetected ändern (sollten sie aber nicht oft) benötigt.
    // Die Initialisierung geschieht jetzt über onEntered.
    console.log(`BarcodeScanner.js: useEffect - Mount/Update`);

    // Cleanup-Funktion wird ausgeführt, wenn die Komponente unmounted wird
    // oder bevor der Effekt erneut läuft (was hier selten sein sollte).
    return () => {
      console.log('BarcodeScanner.js: useEffect - Cleanup');
      // Stelle sicher, dass die Kamera und Listener beim Verlassen des Dialogs gestoppt/entfernt werden.
      stopCamera();
    };
  }, [stopCamera, handleDetected]); // Abhängigkeiten: Nur die Callbacks für den Cleanup

  return (
    // *** 5. `Dialog` anpassen: TransitionProps hinzufügen ***
    <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        TransitionProps={{ onEntered: initializeScanner }} // Hier wird initialisiert!
        // Optional: onExited, um sicherzustellen, dass die Kamera gestoppt wird,
        // aber der useEffect-Cleanup sollte das bereits abdecken.
        // TransitionProps={{ onEntered: initializeScanner, onExited: stopCamera }}
    >
      {/* Mobile Header */}
      {isMobile && (
          <AppBar sx={{ position: 'relative' }}>
              <Toolbar>
                  <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                      <CloseIcon />
                  </IconButton>
                  <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                      Barcode scannen
                  </Typography>
                  <Button color="inherit" onClick={handleClose}>
                      Abbrechen
                  </Button>
              </Toolbar>
          </AppBar>
      )}
      {/* Desktop Title */}
      {!isMobile && (
          <DialogTitle sx={{ m: 0, p: 2, bgcolor: 'primary.main', color: 'white' }}>
              Barcode scannen
              <IconButton aria-label="close" onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}>
                  <CloseIcon />
              </IconButton>
          </DialogTitle>
      )}
      <DialogContent sx={{
          display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
          padding: isMobile ? 0 : 2, overflow: 'hidden', position: 'relative'
      }}>
        {barcodeScanError && (
          <Alert severity="error" sx={{ width: '100%', mb: 1, position: 'absolute', top: isMobile ? '56px' : '10px', left: 0, zIndex: 2 }}>
              {barcodeScanError}
          </Alert>
        )}
        {/* *** 2. Ref zuweisen *** */}
        <Box id="interactive" ref={interactiveRef} className="viewport" sx={{
            width: '100%', height: isMobile ? 'calc(100vh - 56px)' : '480px', maxWidth: '640px',
            position: 'relative', display: 'none', // Starte versteckt, initializeScanner macht es sichtbar
            '& video': { width: '100%', height: '100%', objectFit: 'cover' },
            '& canvas': { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }
        }}>
            <video ref={videoRef} autoPlay playsInline muted></video>
            <canvas className="drawingBuffer" />
        </Box>
      </DialogContent>
      {/* Desktop Actions */}
      {!isMobile && (
          <DialogActions>
              <Button onClick={handleClose}>Abbrechen</Button>
          </DialogActions>
      )}
    </Dialog>
  );
};

export default BarcodeScanner;
